import { BaseCommand } from './baseCommand';
import { ScreenMessage, StreamingMessage } from '../messages/pocket-com/screenMessage';
import { PerformanceService } from '../../remote-control-ng/services/performanceService';
import { RcContext } from '../../remote-control-ng/context/rcContext';
import { RcLoggerService } from '../../remote-control-ng/services/rcLoggerService';
import { IRcDeviceContext, RemoteControlStreamingContext } from '../../remote-control-ng/context/deviceContext';
import { RestartThrottling } from '../../remote-control-ng/services/restartThrottling';
import { ThAckMessage } from '../messages/pocket-com';
import { NgRemoteControlConnection } from 'app/modules/remote-control/services/remote-control.connection';
import { ArrayHelper } from '../utils/arrayHelper';
import { Injector } from '@angular/core';
import { InjectionContextCommand } from '../commandFactory';

@InjectionContextCommand
export class ScreenCommand extends BaseCommand<ScreenMessage> {
    constructor(private injector: Injector) {
        super(ScreenMessage);
    }

    public doProcess(message: ScreenMessage) {
        const packet = message.screenPacket;
        this.injector.get(PerformanceService).screenPackageReceived(packet?.fullscreen);
        if (RcContext.instance.session.isPaused) {
            RcContext.instance.session.resetPause();
            if (!packet?.fullscreen) {
                return;
            }
        }

        if (packet === null) {
            return;
        }
        if (packet.fullscreen) {
            this.injector.get(RcLoggerService).info('[FST] received fullscreen scale: ' + packet.scale);
        }
        // TODO NT: remove direct access to context
        const ctx = RcContext.instance.deviceContext as IRcDeviceContext;
        ctx.saveScale = packet.scale;
        ctx.setRotation(packet.rotation);
        RcContext.instance.deviceContextHandler.updateWithScreenPacket(packet);
        RcContext.instance.session.appUI.setCurrentMonitorRotation(packet.rotation);
        RcContext.instance.deviceScreenTransformService?.updateTransformAndScreen(packet);
    }
}

@InjectionContextCommand
export class StreamingCommand extends BaseCommand<StreamingMessage> {
    constructor(private injector: Injector) {
        super(StreamingMessage);
    }

    public doProcess(message: StreamingMessage): void {
        const deviceContext = <RemoteControlStreamingContext>RcContext.instance.deviceContext;
        const restartThrottle = this.injector.get(RestartThrottling);

        // remote-control.connection.ts onFullScreenMessage()
        if (deviceContext.lastChangeTimestamp < 0) {
            deviceContext.lastChangeTimestamp = Date.now();
            restartThrottle.sendRestart(RcContext.instance);
            return;
        }

        // nxt-remote-control-screen.service.ts processFullScreenMessage
        const packet = message.screenPacket;
        const mcrcLog = this.injector.get(RcLoggerService);
        const deviceViewHandler = deviceContext.deviceViewHandler;
        mcrcLog.debug(
            'Screen message: isKeyFrame = ' +
            packet.fullscreen +
            ', orientation = ' +
            packet.rotation +
            ', ack = ' +
            packet.ackRequired
        );

        deviceContext.networkInfo.bytes += (<StreamingMessage>message).messageBytes;
        deviceContext.networkInfo.frames++;
        if (packet.ackRequired) {
            RcContext.instance.session.sendMessage(new ThAckMessage());
            deviceViewHandler.sendAck();
        }

        deviceContext.rotation = packet.rotation;
        const [changed, scale] = restartThrottle.updateScale(packet.scale, RcContext.instance);
        if (changed) {
          deviceContext.scale = deviceContext.remoteControlConnection.getScale() / 100;
        }

        // check if it's a new video stream which starts with a ftyp header. DS creates a new stream when device screen orientation changes, so that video player can handle it properly
        const isKeyFrame = packet.fullscreen;
        const buf = packet.mp4;
        if (isKeyFrame) {
            if (ArrayHelper.startWith(buf, 4, 'ftyp')) {
                (<NgRemoteControlConnection>deviceContext.remoteControlConnection).startStreamingRC(deviceContext.device.deviceId);

                mcrcLog.info('New stream, restart player');
                deviceViewHandler.stopVideoPlayer();
                deviceContext.mediaSource = deviceViewHandler.buildMediaSource();
                deviceContext.video.src = URL.createObjectURL(deviceContext.mediaSource);
            } else if (deviceContext.networkInfo.recentKeyframes.push(Date.now()) > 4) {
                deviceContext.networkInfo.recentKeyframes.shift();
            }
        }

        if (deviceContext.videoPlayerReady) {
            deviceViewHandler.updateBuffer(buf, isKeyFrame);
        } else if (isKeyFrame || deviceContext.mediaSource) {
            // for non key frames, drop them unless a key frame has been received (hence this.mediaSource != null)
            deviceViewHandler.queueBuffer(buf);
        }
    }
}
